import * as RadixMenu from "@radix-ui/react-dropdown-menu";
import { Check, ChevronRight } from "lucide-react";
import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * DropdownMenu — Radix primitive wrapped in our Apple-ish surface:
 * elevated card with hairline border, generous padding, 13~14 px items,
 * subtle hover. Animations capped at 140 ms so the menu never feels
 * "in the way" during fast operator workflows.
 */

export const DropdownMenu = RadixMenu.Root;
export const DropdownMenuTrigger = RadixMenu.Trigger;
export const DropdownMenuGroup = RadixMenu.Group;
export const DropdownMenuPortal = RadixMenu.Portal;
export const DropdownMenuSub = RadixMenu.Sub;
export const DropdownMenuRadioGroup = RadixMenu.RadioGroup;

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Content>
>(({ className, sideOffset = 6, ...p }, ref) => (
  <RadixMenu.Portal>
    <RadixMenu.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 min-w-[200px] p-1 rounded-[var(--radius-lg)]",
        "bg-[var(--bg-elevated)] border border-[var(--border)] shadow-[var(--shadow-3)]",
        "text-[var(--fg)]",
        "data-[state=open]:animate-[fade-in_var(--motion-fast)_var(--ease-out-soft)_both]",
      )}
      {...p}
    />
  </RadixMenu.Portal>
));
DropdownMenuContent.displayName = "DropdownMenuContent";

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Item> & { destructive?: boolean }
>(({ className, destructive, ...p }, ref) => (
  <RadixMenu.Item
    ref={ref}
    className={cn(
      "relative flex items-center gap-2.5 px-2.5 h-9 rounded-[var(--radius-md)]",
      "text-[14px] tracking-[-0.005em] cursor-default select-none outline-none",
      "transition-colors duration-[var(--motion-fast)] ease-[var(--ease-out-soft)]",
      "data-[highlighted]:bg-[var(--bg-hover)]",
      "data-[disabled]:opacity-40 data-[disabled]:pointer-events-none",
      destructive && "text-[var(--danger)] data-[highlighted]:bg-[color-mix(in_oklab,var(--danger)_10%,transparent)]",
      className,
    )}
    {...p}
  />
));
DropdownMenuItem.displayName = "DropdownMenuItem";

export const DropdownMenuLabel = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Label>
>(({ className, ...p }, ref) => (
  <RadixMenu.Label
    ref={ref}
    className={cn(
      "px-2.5 pt-2 pb-1 text-[11px] uppercase tracking-[0.07em] text-[var(--fg-subtle)] font-semibold",
      className,
    )}
    {...p}
  />
));
DropdownMenuLabel.displayName = "DropdownMenuLabel";

export const DropdownMenuSeparator = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.Separator>
>(({ className, ...p }, ref) => (
  <RadixMenu.Separator
    ref={ref}
    className={cn("h-px my-1 -mx-1 bg-[var(--separator)]", className)}
    {...p}
  />
));
DropdownMenuSeparator.displayName = "DropdownMenuSeparator";

export const DropdownMenuShortcut = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <span
    className={cn(
      "ml-auto pl-3 text-[11.5px] tabular-nums text-[var(--fg-subtle)]",
      className,
    )}
  >
    {children}
  </span>
);

export const DropdownMenuCheckboxItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.CheckboxItem>
>(({ className, children, ...p }, ref) => (
  <RadixMenu.CheckboxItem
    ref={ref}
    className={cn(
      "relative flex items-center gap-2 px-2.5 h-9 pr-3 pl-7 rounded-[var(--radius-md)]",
      "text-[14px] cursor-default select-none outline-none",
      "data-[highlighted]:bg-[var(--bg-hover)]",
      className,
    )}
    {...p}
  >
    <span className="absolute left-2 inline-flex h-4 w-4 items-center justify-center">
      <RadixMenu.ItemIndicator>
        <Check size={13} strokeWidth={2.4} />
      </RadixMenu.ItemIndicator>
    </span>
    {children}
  </RadixMenu.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = "DropdownMenuCheckboxItem";

export const DropdownMenuSubTrigger = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.SubTrigger>
>(({ className, children, ...p }, ref) => (
  <RadixMenu.SubTrigger
    ref={ref}
    className={cn(
      "flex items-center gap-2 px-2.5 h-9 rounded-[var(--radius-md)]",
      "text-[14px] outline-none data-[highlighted]:bg-[var(--bg-hover)]",
      className,
    )}
    {...p}
  >
    {children}
    <ChevronRight size={14} className="ml-auto opacity-60" />
  </RadixMenu.SubTrigger>
));
DropdownMenuSubTrigger.displayName = "DropdownMenuSubTrigger";

export const DropdownMenuSubContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof RadixMenu.SubContent>
>(({ className, ...p }, ref) => (
  <RadixMenu.SubContent
    ref={ref}
    className={cn(
      "z-50 min-w-[180px] p-1 rounded-[var(--radius-lg)]",
      "bg-[var(--bg-elevated)] border border-[var(--border)] shadow-[var(--shadow-3)]",
      className,
    )}
    {...p}
  />
));
DropdownMenuSubContent.displayName = "DropdownMenuSubContent";
